/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import {ActionReducerMapBuilder, AsyncThunk, current} from '@reduxjs/toolkit';
import {clsx, type ClassValue} from 'clsx';
import moment from 'moment';
import {twMerge} from 'tailwind-merge';
import SparkMD5 from 'spark-md5';
import {Column} from 'components/table/Column';
import {AsyncResult, ListResult, SelectOption, SingleResult, TabDefinition} from './types';
import {BLANK_OPTION_LABEL} from './constants';

// Export cn using the default twMerge function
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const deconstructSagaPayload = (payload: Record<string, any>): Record<string, any> => {
  return {
    data: payload?.data,
    onSuccess: payload?.onSuccess || (() => {}),
    onFailed: payload?.onFailed || (() => {}),
  };
};

export const handleError = (error: Record<string, any>): string => {
  switch (error?.status) {
    case 401:
      return error?.message;
    case 400:
      return error?.message[0];
    default:
      return 'Something went wrong!';
  }
};
type PromiseWithResolversPolyfill = <T>() => {promise: Promise<T>; resolve(t: T); reject(e: any)};
export const promiseExplode = (() => {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return {resolve, reject, promise};
}) as PromiseWithResolversPolyfill;

export const newOption = (value: string | number | null, label?: string | number) => {
  const result = {value} as {value: any; label: any; options: any[]};
  result.label = label || value;
  return result;
};

export const prependBlankOption = (
  optionList: SelectOption<string | number | null>[],
): SelectOption<string | number | null>[] => {
  optionList.unshift(newOption(null, BLANK_OPTION_LABEL));
  return optionList;
};

export const fromOption = (onChange: (newValue: any) => any = x => x) => {
  return (newValue: any) => onChange(newValue?.value);
};

export const selectStyles = {
  menu: provided => ({
    ...provided,
    zIndex: 9999,
    backgroundColor: 'hsl(var(--popover))',
    borderColor: 'hsl(var(--border))',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '0.375rem',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,.1), 0 2px 4px -2px rgba(0,0,0,.1)',
  }),
  menuList: provided => ({
    ...provided,
    padding: '4px',
  }),
  control: provided => ({
    ...provided,
    backgroundColor: 'hsl(var(--background))',
    borderColor: 'hsl(var(--border))',
    color: 'hsl(var(--foreground))',
    borderStyle: 'solid',
    outline: 'none',
    borderWidth: '1px',
    fontSize: 14,
    fontWeight: 'normal',
    paddingLeft: 4,
    width: '100%',
    minHeight: 40,
    ':hover': {
      borderColor: 'hsl(var(--ring))',
    },
  }),
  singleValue: provided => ({
    ...provided,
    color: 'hsl(var(--foreground))',
  }),
  input: provided => ({
    ...provided,
    color: 'hsl(var(--foreground))',
  }),
  placeholder: provided => ({
    ...provided,
    color: 'hsl(var(--muted-foreground))',
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: 'hsl(var(--muted-foreground))',
    padding: '0.375rem',
    transition: 'transform .25s ease',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
    ':hover': {
      color: 'hsl(var(--foreground))',
    },
  }),
  indicatorSeparator: provided => ({
    ...provided,
    backgroundColor: 'hsl(var(--border))',
  }),
  option: (styles, {isSelected, isFocused}) => {
    return {
      ...styles,
      backgroundColor: isSelected ? 'hsl(var(--primary))' : isFocused ? 'hsl(var(--accent))' : 'transparent',
      color: isSelected || isFocused ? 'hsl(var(--primary-foreground))' : 'hsl(var(--popover-foreground))',
      ':hover': {
        backgroundColor: 'hsl(var(--accent))',
        color: 'hsl(var(--accent-foreground))',
      },
      width: '100%',
      cursor: 'pointer',
      fontSize: 14,
      fontWeight: 'normal',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      borderRadius: '0.25rem',
    };
  },
  noOptionsMessage: provided => ({
    ...provided,
    color: 'hsl(var(--muted-foreground))',
  }),
  loadingMessage: provided => ({
    ...provided,
    color: 'hsl(var(--muted-foreground))',
  }),
};

const DIGITAL_SHELF_CACHE = 'DIGITAL_SHELF_CACHE'; // can be an array of keys
const TTL = 1000 * 60 * 60 * 24 * 14;

const currentTime = () => {
  return Date.now();
};

export const getCached = () => {
  let cache = {
    data: {},
    nextCleanup: new Date().getTime() + TTL,
  };

  try {
    const data = localStorage.getItem(DIGITAL_SHELF_CACHE);
    if (data) {
      cache = JSON.parse(data);
    }
  } catch (e) {
    console.error(e);
  }

  return cache;
};
const cleanUpStorage = data => {
  let isDeleted;
  let oldest;
  let oldestKey;

  // eslint-disable-next-line guard-for-in,no-restricted-syntax
  for (const key in data) {
    const {ttl} = data[key];
    if (ttl && ttl <= currentTime()) {
      delete data[key];
      isDeleted = true;
    }

    if (!oldest || oldest > ttl) {
      oldest = ttl;
      oldestKey = key;
    }
  }

  if (!isDeleted && oldestKey) {
    delete data[oldestKey];
  }

  localStorage.setItem(
    DIGITAL_SHELF_CACHE,
    JSON.stringify({
      data,
      nextCleanup: currentTime() + TTL,
    }),
  );
};

export const setToCache = (key, value) => {
  const cached = getCached();
  const {data} = cached;
  const item = {
    id: key,
    ttl: new Date().getTime() + TTL,
    value,
  };
  data[key] = item;

  try {
    localStorage.setItem(DIGITAL_SHELF_CACHE, JSON.stringify(cached));
  } catch (e) {
    cleanUpStorage(data);
  }
};

// https://stackoverflow.com/questions/10420352/converting-file-size-in-bytes-to-human-readable-string
export const humanFileSize = size => {
  const i = Math.floor(Math.log(size) / Math.log(1024));
  return `${(size / 1024 ** i).toFixed(2)} ${['B', 'kB', 'MB', 'GB', 'TB'][i]}`;
};

export const formatTime = str => moment(str).format('Y/M/D HH:mm');
export const formatDate = str => moment(str).format('Y/M/D');
export const formatDateUTC = str => moment(str).utc(false).format('MM-DD-YY');
export const formatMoney = str => Number(str).toLocaleString('en-US', {style: 'currency', currency: 'USD'});
export const formatRatioAsPercentage = (value?: number): string => `${((value || 0) * 100.0).toFixed(2)} %`;
export const formatPercentage = (value?: number): string => `${(value || 0).toFixed(2)} %`;

export const round = (n, decimals = 1) => {
  const m = 10 ** decimals;
  return Math.round(n * m) / m;
};

// https://stackoverflow.com/questions/6443990/javascript-calculate-brighter-colour
export const brighten = (color: string, percent: number) => {
  const r = parseInt(color.substring(1, 3), 16);
  const g = parseInt(color.substring(3, 5), 16);
  const b = parseInt(color.substring(5, 7), 16);

  return `#${Math.min(255, Math.floor(r * percent)).toString(16)}${Math.min(255, Math.floor(g * percent)).toString(
    16,
  )}${Math.min(255, Math.floor(b * percent)).toString(16)}`;
};

export const removeUndefined = (obj: Object) => {
  Object.keys(obj).forEach(
    key => (obj[key] === undefined || obj[key] === null || (Array.isArray(obj) && !obj[key].length)) && delete obj[key],
  );
};

export const humanReadableFileSize = (bytes: number = 0, fractionDigits: number = 2) => {
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let fileSize = bytes;

  while (fileSize >= 1024 && i < units.length - 1) {
    fileSize /= 1024;
    i += 1;
  }

  return `${fileSize.toFixed(fractionDigits)} ${units[i]}`;
};

export const getPropertyValue = (o: object, key: string) => {
  return key.split('.').reduce((n: object, k: string) => {
    return n && n[k];
  }, o);
};

export const stripUndefined: <T extends object>(o: T) => T = obj => {
  return Object.fromEntries(Object.entries(obj).filter(kv => kv[1] !== undefined)) as any;
};

export function downloadFile(toObjectUrl: Blob | MediaSource | string, fileName: string) {
  if (typeof toObjectUrl === 'string') {
    // create "a" HTML element with href to file & click
    const link = document.createElement('a');
    link.href = toObjectUrl;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();

    // clean up "a" element & remove ObjectURL
    document.body.removeChild(link);
  } else {
    const href = URL.createObjectURL(toObjectUrl);
    // create "a" HTML element with href to file & click
    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', fileName); // or any other extension
    document.body.appendChild(link);
    link.click();

    // clean up "a" element & remove ObjectURL
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  }
}

const RESULT_STALE_ID = Symbol('RESULT_STALE_ID');
export function addResultCases<T>(builder: ActionReducerMapBuilder<T>, thunk: AsyncThunk<any, any, any>, key: keyof T) {
  builder
    .addCase(thunk.pending, (state, {meta}) => {
      const s = state as {[K in typeof key]: ListResult<any>};
      const staleRes = s[key]?.[RESULT_STALE_ID];
      const update = state as {[K in typeof key]: ListResult<any>};
      // update[key] = {as: 'loading', requestId: meta.requestId};
      update[key] = {...update[key], as: 'loading', requestId: meta.requestId};
      if (staleRes) update[key][RESULT_STALE_ID] = current(staleRes);
    })
    .addCase(thunk.rejected, (state, {payload, meta}) => {
      const s = state as {[K in typeof key]: ListResult<any>};
      if (s[key].as !== 'loading') return;
      if (s[key].requestId !== meta.requestId) return;
      const staleRes = s[key]?.[RESULT_STALE_ID];
      const update = s as {[K in typeof key]: ListResult<any>};
      update[key] = {as: 'error', error: payload as object};
      if (staleRes) update[key][RESULT_STALE_ID] = current(staleRes);
    })
    .addCase(thunk.fulfilled, (state, {payload, meta}) => {
      const s = state as {[K in typeof key]: ListResult<any>};
      if (s[key].as !== 'loading') return;
      if (s[key].requestId !== meta.requestId) return;
      const update = s as {[K in typeof key]: ListResult<any>};
      update[key] = {as: 'success', ...payload};
      update[key][RESULT_STALE_ID] = payload;
    });
}
const EMPTY_RESULT = {as: 'success', count: 0, results: [], value: null} as const;
export function getResult<T, L extends ListResult<T> | SingleResult<T>>(result: L): Extract<L, {as: 'success'}> {
  if (!result) return EMPTY_RESULT as any;
  if (result.as === 'success') return result as any;
  if (result[RESULT_STALE_ID]) return (result as any)[RESULT_STALE_ID] as any;
  return EMPTY_RESULT as any;
}
export function readError<T, L extends ListResult<T> | SingleResult<T>>(
  result: L,
  fallback?: string,
): string | undefined {
  if (result?.as !== 'error') return;

  const err = result.error;
  if (typeof err === 'string') return err;
  if ((err as any)?.message) return (err as any).message;
  if ((err as any)?.detail) return (err as any).detail;
  return fallback;
}
export function defaultFormErrors(formHookSetError: any) {
  return (e: any) => {
    if (!(e instanceof Object)) return 'Failed to save';
    if ('message' in e) return e.message as string;
    let message = 'Failed to save:\n';
    Object.entries(e).forEach(([key, value]) => {
      formHookSetError(key as keyof any, {type: 'server', message: String(value)});
      message += `${key}: ${value}\n`;
    });
    return message;
  };
}
/**
 * Helper to extract error from useMutation result and pass it to defaultFormErrors
 * @param setError form setError function
 * @returns a function that takes a mutation result and extracts the error to pass to defaultFormErrors
 */
export function defaultResultFormErrors<T extends {error?: any}>(setError: (name: any, error: any) => void) {
  return (res: T) => defaultFormErrors(setError)(res.error);
}
export function matchResult<
  T,
  L extends ListResult<T> | SingleResult<T> | AsyncResult<T>,
  H extends {[Key in L['as']]: (result: Extract<L, {as: Key}>) => any},
>(result: L, handlers: H): ReturnType<H[L['as']]> {
  return handlers[result.as](result as any);
}
export function isType<T, L extends ListResult<T> | SingleResult<T>, K extends L['as']>(
  result: L,
  type: K,
): result is Extract<L, {as: K}> {
  return result?.as === type;
}
export function isNot<T, L extends ListResult<T> | SingleResult<T>, K extends L['as']>(
  result: L,
  type: K,
): result is Exclude<L, {as: K}> {
  return result?.as !== type;
}

export const getTitle = (name: string) => {
  return name
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const sanitizeFilename = (namePart: string) => {
  return namePart
    .replace(/[^a-zA-Z0-9._\-\s]/g, '')
    .trim()
    .slice(0, 20);
};

export const defineTabDef: <T extends Record<string, TabDefinition>>(tabDef: T) => T = tabDef => {
  return tabDef;
};

/** Reject after `timeout` ms if `promise` does not resolve/reject in that time. */
export const timeoutPromise = (promise: Promise<any>, timeout: number, message?: string): Promise<any> => {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      const timeoutHandle = setTimeout(() => {
        clearTimeout(timeoutHandle);
        reject(message || `Timeout after ${timeout} ms`);
      }, timeout);
    }),
  ]);
};

export async function calculateFileHash(file: File): Promise<string> {
  const hasher = new SparkMD5.ArrayBuffer();
  const reader = file.stream().getReader();

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const {done, value} = await reader.read();
    if (done) break;
    hasher.append(value as unknown as ArrayBuffer);
  }

  return hasher.end();
}

export const addConditionalColumn = (
  originalColumns: Column[],
  column: Column,
  columnCondition: boolean,
  columnIndexFn: (col: Column) => boolean,
) => {
  if (!columnCondition) return originalColumns;
  const insertIndex = originalColumns.findIndex(columnIndexFn);
  return [...originalColumns.slice(0, insertIndex), column, ...originalColumns.slice(insertIndex)];
};
